import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { localStorageKeys, useLocalStorage } from "../../../utils/localStorageItems";
import HeaderView from "../../../components/admin/headerView";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import { APP_CONFIG } from "../../../constants/constants";
import AppSearchBox from "../../../components/searchBox";
import React, { useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { setListingQueryParams, setModalsData, TListingQueryParams, TModalsState } from "../../../api/store/commonSlice";
import { EditListingColumnsButton, ModalEditListingColumns, TListingColumn } from "../../../components/modalEditListingColumns";
import FormFieldDateRangePicker from "../../camera-admin/video-history/dateRangePicker";
import { dbTimeToJsTime, formattedDateTimeBasedOnTimezone, jsTimeToDBTime } from "../../../utils/dateTime";
import { useAccountTimezone } from "../../../hooks/timezone";
import { DateRange } from "rsuite/esm/DateRangePicker";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { AdminFormFieldDropdownButton, AdminFormFieldSubmit, TSelectboxOption } from "../../../components/admin/formFields";
import svgIcons from "../../../assets/svg/svgIcons";
import SortingFilter from "../../../components/sortingFilter";
import AdminTable from "../../../components/adminTable";
import { TAdminTableRowData } from "../../../components/types";
import { routeUrls } from "../../../navigation/routeUrls";
import Pagination, { TPaginationSelected } from "../../../components/pagination";
import { useGetIncidentListingQuery, useLazyGetIncidentConfigQuery, useLazyGetExportIncidentListingQuery } from "../../../api/network/incidentApiServices";
import { IncidentItem, INCIDENT_DISPOSITIONS, TIncidentDisposition, IncidentRecord } from "./type";
import { toast } from "react-toastify";
import { convertKmphTo, getDistanceByUnit } from "../../../utils/distance";
import { DISTANCE_UNIT_VALUES, DISTANCE_UNITS_OPTIONS, SPEED_UNIT_VALUES, SPEED_UNITS, SPEED_UNITS_OPTIONS } from "../../admin-details/account/constants";
import { useLoggedInUserData } from "../../../utils/user";
import CheckableListItem from "../../../components/checkableListItem";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import { subDays } from "date-fns";
import HierarchyGroupSelector from "../../../components/common/hierarchyGroupSelector";
import ModalGroupSelector from "../../../components/common/modalGroupSelector";
import { TGroupSelectorItem } from "../../../components/common/groupSelector.type";
import { LegacyGroupEntityListingPayload } from "../../../api/types/Group";
import { useOrganizationGroupsQuery } from "../../../api/network/adminApiServices";

const dispositionColors = Object.fromEntries(
    Object.values(INCIDENT_DISPOSITIONS).map(({ label, color }) => [label, color])
);

const ScreenIncidentListing = () => {
    const { t } = useTranslation("translation", {
        keyPrefix: "admins.incident.incident_review",
    });
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const thisOrgIncidentReviewAllowed = useLoggedInUserData("incidentreviewallowed");
    const { getUserACLAccessLevel } = useACL();
    useEffect(() => {
        if (
            thisOrgIncidentReviewAllowed !== "1" ||
            getUserACLAccessLevel(ACL_NAMES.ACL_INCIDENT_REVIEW) < ROLE_ACCESS_LEVELS.READ
        ) {
            navigate(routeUrls.dashboard);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [showIncidentTypeDropdown, setIncidentTypeDropdown] = useState(false);
    const [showDispostionDropdown, setDispositionDropdown] = useState(false);

    const { getLocalStorageItem } = useLocalStorage();

    const listingQueryParams: TListingQueryParams
        = useSelector(
            (state: any) => state.commonReducer.listingQueryParams
        );
    const incidentQueryParams = listingQueryParams.incidentsReview;
    const { convertToTZForDateFns, tzForDateFns } =
        useAccountTimezone();
    const { t: tAdmin } = useTranslation("translation", {
        keyPrefix: "dashboard.sidemenu.admins",
    });
    // check if group hierarchy is enabled
    const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");
    const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
    const modalsState: TModalsState = useSelector((state: any) => state.commonReducer.modals);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [filteredGroupData, setFilteredGroupData] = useState<TGroupSelectorItem[]>([]);
    // fetch groups data
    const {
        data: dataOrgGroups,
        isFetching: isFetchingOrgGroups,
        error: errorOrgGroups,
    } = useOrganizationGroupsQuery(
        {
            accountid: `${thisUserOrganizationId}`,
            page: 1,
            page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
            search: "",
        } as LegacyGroupEntityListingPayload,
        {
            refetchOnMountOrArgChange: true,
        }
    );

    useEffect(() => {
        setFilteredGroupData(
            (accountHasGroupHierarchyEnabled === "1"
                ? []
                : (dataOrgGroups?.results?.map((group) => {
                    return {
                        groupid: group.groupid,
                        description: group.description,
                        is_hgroup: false,
                        checked: true,
                    };
                }) ?? [])) as TGroupSelectorItem[]
        );
    }, [dataOrgGroups]);

    const all_columns = [
        "Asset Id",
        "Asset name",
        "Date & Time",
        "Incident Type",
        "Disposition",
        "Driver Name",
        "Address",
        "Speed",
        "Speed Limit",
        "Odometer",
        "Sat Count",
        "Hdop"
    ]
    const columnOrderingFields: Record<string, string> = {
        "Asset Id": "deviceid",
        "Asset name": "asset_name",
        "Date & Time": "timestamp",
        "Incident Type": "statuscode",
        "Disposition": "incident_disposition",
        "Driver Name": "driverid",
        "Address": "address",
        "Speed": "speedkph",
        "Speed Limit": "speedlimitkph",
        "Odometer": "odometerkm",
        "Sat Count": "satellitecount",
        "Hdop": "hdop"
    }

    const [columns, setColumns] = useState<TListingColumn[]>(() => {
        //savedColumns contain the visible columns in order
        const savedColumns = getLocalStorageItem(
            localStorageKeys.columns.IncidentReview
        );
        if (!savedColumns?.length) {
            return all_columns.map((name: string) => ({
                name,
                show: true,
            }));
        }
        const orderedColumns = [
            ...savedColumns,
            ...all_columns.filter(
                (name: string) => !savedColumns.includes(name)
            ),
        ];
        return orderedColumns.map((name: string) => ({
            name,
            show: savedColumns.includes(name),
        }));
    });

    const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
        dispatch(
            setListingQueryParams({
                ...listingQueryParams,
                incidentsReview: { ...incidentQueryParams, page: 1, search: value },
            })
        );
    }, 500);

    const selectedGroupIds = useMemo(() => {
    if (accountHasGroupHierarchyEnabled === "1") {
        return filteredGroupData
        .filter((group) => group.is_hgroup && group.checked && group.groupid != null)
        .map((group) => group.groupid);
    }
    return selectedGroups;
    }, [accountHasGroupHierarchyEnabled, filteredGroupData, selectedGroups]);
    
    const incidentListingParams = {
        ...incidentQueryParams,
        group_ids: selectedGroupIds.join(","),
    };

    const {
        data: tableResults,
        isFetching: isLoadingTable,
    } = useGetIncidentListingQuery(incidentListingParams);
    const [
        exportIncidents,
        { isFetching: isExporting }
    ] = useLazyGetExportIncidentListingQuery();

    const [getIncidentTypes, { data: incidentTypes, isFetching: isLoadingIncidentTypes }] = useLazyGetIncidentConfigQuery();

    const handleRangeChange = (value: [any, any] | null) => {
        if (value) {
            const [start, end] = value;
            if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
                return;
            }
            const utcStartDate = fromZonedTime(
                start,
                convertToTZForDateFns(tzForDateFns)
            );
            const jsTimeStart = new Date(utcStartDate).getTime();
            const utcEndDate = fromZonedTime(
                end,
                convertToTZForDateFns(tzForDateFns)
            );
            const jsTimeEnd = new Date(utcEndDate).getTime();
            dispatch(
                setListingQueryParams({
                    ...listingQueryParams,
                    incidentsReview: {
                        ...incidentQueryParams,
                        start_time: jsTimeToDBTime(jsTimeStart),
                        end_time: jsTimeToDBTime(jsTimeEnd),
                        page: 1,
                    },
                })
            );
        }
    };

    const handleOrderingChange = (ordering?: string) => {
        dispatch(
            setListingQueryParams({
                ...listingQueryParams,
                incidentsReview: {
                    ...incidentQueryParams,
                    ordering,
                    page: 1,
                },
            })
        );
    };

    useEffect(() => {
        getIncidentTypes({ active_only: true })
        if (incidentQueryParams?.start_time && incidentQueryParams?.end_time)
            return;
        const timezone = convertToTZForDateFns(tzForDateFns);
        const nowInAccountTimezone = toZonedTime(new Date(), timezone);
        const startInAccountTimezone = subDays(nowInAccountTimezone, 1);
        const utcStartDate = fromZonedTime(
            startInAccountTimezone,
            timezone
        );
        const utcEndDate = fromZonedTime(
            nowInAccountTimezone,
            timezone
        );
        const jsTimeStart = new Date(utcStartDate).getTime();
        const jsTimeEnd = new Date(utcEndDate).getTime();
        dispatch(
            setListingQueryParams({
                ...listingQueryParams,
                incidentsReview: {
                    ...incidentQueryParams,
                    start_time: jsTimeToDBTime(jsTimeStart),
                    end_time: jsTimeToDBTime(jsTimeEnd),
                    page: 1,
                },
            })
        );
    }, [])

    const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
    const orgDistanceUnit = useLoggedInUserData("distanceUnits") as DISTANCE_UNIT_VALUES;

    const speedOption =
        SPEED_UNITS_OPTIONS.find(option => option.value === orgSpeedUnit) ??
        SPEED_UNITS_OPTIONS[0];

    const distanceOption =
        DISTANCE_UNITS_OPTIONS.find(option => option.value === orgDistanceUnit) ??
        DISTANCE_UNITS_OPTIONS[0];

    const formatValue = (value: number, label: string) =>
        isNaN(value) || value < 0
            ? "Not Available"
            : `${Math.round(value)} ${label}`;

    const convertKph = (speedKph: number) =>
        formatValue(
            convertKmphTo(speedOption.unit, speedKph),
            speedOption.label
        );

    const convertKm = (distanceKm: number) =>
        formatValue(
            getDistanceByUnit(distanceKm, orgDistanceUnit),
            distanceOption.label
        );

    const tableData: TAdminTableRowData[] = useMemo(() => {
        return !!tableResults
            ? (tableResults.results as IncidentRecord[] || []).map(
                (item, index) => {
                    const cellDataByColumn = {
                        deviceid: item.deviceid,
                        asset_name: item.asset_name,
                        timestamp:
                            item?.timestamp && !!tzForDateFns
                                ? formattedDateTimeBasedOnTimezone(
                                    item.timestamp * 1000,
                                    tzForDateFns
                                )
                                : "-",
                        statuscode: item.incident_type,
                        incident_disposition:
                            INCIDENT_DISPOSITIONS[
                                item.disposition as TIncidentDisposition
                            ]?.label,
                        driverid: item.driver_name,
                        address: item.address,
                        speedkph: convertKph(item.speedkph),
                        speedlimitkph: convertKph(item.speedlimitkph),
                        odometerkm: convertKm(item.odometerkm),
                        satellitecount: item.sat_count,
                        hdop: item.hdop.toFixed(2),
                    }
                    return {
                        navLink: item.recordid
                            ? `${routeUrls.dashboardChildren.incident_review_detail}/${item.recordid}`
                            : "",
                        tableRowId: `event-${item.recordid}`,
                        stateData: item,
                        key: `event-${item.recordid}-${index}`,

                        cellData: columns
                            .filter((column) => column.show)
                            .map((column: TListingColumn) => {
                                const field = columnOrderingFields[column.name];
                                return cellDataByColumn[field as keyof typeof cellDataByColumn]
                            }),
                    }
                }
            )
            : [];
    }, [columns, tableResults?.results, tzForDateFns]);

    const handleExportData = async () => {
        if (incidentQueryParams.start_time && incidentQueryParams.end_time) {
            try {
                const { start_time, end_time, page, page_size, disposition, statuscodes } = incidentQueryParams;
                const response = await exportIncidents({
                    start_time,
                    end_time,
                    page,
                    page_size,
                    csv_export: 1,
                    statuscodes,
                    disposition,
                    group_ids: incidentListingParams.group_ids,
                }).unwrap();
                const blob = new Blob([response], {
                    type: "text/csv;charset=utf-8;",
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "incidents_listing.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(t("csv_download_successful"))
            } catch (error) {
                console.log(error);
                toast.error(t("csv_download_failed"));
            }
        }
        else {
            toast.error(t("csv_time_missing"));
        }
    };

    const onGroupSelectionChange = (selectedGroups: string[]) => {
        // if selection is different from filteredGroupData, then reset the filter
        const divergence1 = filteredGroupData
            .filter((group) => group.is_hgroup && group.checked)
            .filter((group) => !selectedGroups.includes(group.groupid));
        const divergence2 = selectedGroups.filter(
            (group) =>
                !filteredGroupData
                    .filter((group) => group.is_hgroup && group.checked)
                    .map((group) => group.groupid)
                    .includes(group)
        );
        if (divergence1.length > 0 || divergence2.length > 0) {
            setFilteredGroupData(
                selectedGroups.map((group) => {
                    return {
                        groupid: group,
                        checked: true,
                        is_hgroup: true,
                    };
                }) as TGroupSelectorItem[] // used `any` to avoid type mismatch as a workaround
            );
        }
    };

    // set selected groups
    useEffect(() => {
        if (accountHasGroupHierarchyEnabled === "1") return;
        if (!!dataOrgGroups && !isFetchingOrgGroups) {
            setSelectedGroups(
                dataOrgGroups?.results?.map((group) => {
                    return group.groupid;
                }) ?? ([] as string[])
            );
        }
    }, [dataOrgGroups, isFetchingOrgGroups]);
    const MemoizedGroupSelector = useMemo(() => {
        return (
            <>
                {accountHasGroupHierarchyEnabled === "1" ? (
                    <>
                        {modalsState.showHierarchyGroupSelector && (
                            <HierarchyGroupSelector
                                initiallySelectedGroups={filteredGroupData
                                    .filter((group) => group.is_hgroup && group.checked)
                                    .map((group) => group.groupid)}
                                onSelectionChange={(selectedGroups) => {
                                    onGroupSelectionChange(selectedGroups);
                                }}
                            />
                        )}
                    </>
                ) : (
                    <ModalGroupSelector
                        hideGroupAssetToggle={true}
                        allGroups={dataOrgGroups?.results ?? []}
                        filteredGroupData={filteredGroupData}
                        setFilteredGroupData={(e: TGroupSelectorItem[]) => {
                            if (APP_CONFIG.DEBUG.ALL) console.log("setFilteredGroupData", e);
                            return setSelectedGroups(
                                e
                                    ?.filter((group) => group.checked)
                                    ?.map((group) => group.groupid)
                            );
                        }}
                        key={`group-selector-${isFetchingOrgGroups.toString()}`}
                    />
                )}
            </>
        );
    }, [
        modalsState.showHierarchyGroupSelector,
        accountHasGroupHierarchyEnabled,
        dataOrgGroups,
        filteredGroupData,
    ]);

    return (
        <>
            {MemoizedGroupSelector}
            <HeaderView title={t("heading")} />
            <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-4`}>
                <div className="flex flex-row justify-between">
                    <ListingPageSubHeader
                        heading={t("sub_heading")} />
                    <AdminFormFieldSubmit type="button"
                        variant="primary-like"
                        label={<span className="flex justify-center whitespace-nowrap px-2">
                            {t("export")}
                            <img
                                className="ml-2 grayscale"
                                src={svgIcons.Upload}
                                alt={t("export")}
                            />
                        </span>}
                        onClick={handleExportData}
                        customWrapperClass="mx-2"
                        disabled={tableData.length > 0 ? false : true}
                    />
                </div>

                <div className="flex flex flex-col md:flex-row gap-2 mt-6 items-center justify-between gap-4">
                    <span className="text-lg font-bold leading-6">{t("listing_heading")}</span>

                    <div className="flex flex-row gap-3 justify-end items-center">
                        <FormFieldDateRangePicker
                            className="flex h-10"
                            value={
                                (incidentQueryParams?.start_time &&
                                    incidentQueryParams?.end_time) ?
                                    ([
                                        new Date(
                                            formattedDateTimeBasedOnTimezone?.(
                                                dbTimeToJsTime(
                                                    incidentQueryParams?.start_time
                                                ),
                                                tzForDateFns,
                                                APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                                            )
                                        ),
                                        new Date(
                                            formattedDateTimeBasedOnTimezone?.(
                                                dbTimeToJsTime(
                                                    incidentQueryParams?.end_time
                                                ),
                                                tzForDateFns,
                                                APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                                            )
                                        ),
                                    ] as DateRange) :
                                    null
                            }
                            onChange={handleRangeChange}
                        />

                        <div
                            className="relative"
                            onClick={() => setIncidentTypeDropdown(!showIncidentTypeDropdown)}
                        >
                            <AdminFormFieldDropdownButton
                                wrapperClass={`border-accent-blue-pale text-accent-gray-800 hover:bg-accent-blue-dark hover:text-white${showIncidentTypeDropdown ? " z-modal" : ""
                                    }`}
                                label="Incident Types"
                                showDropdownIcon={showIncidentTypeDropdown}
                                setShowDropdownIcon={setIncidentTypeDropdown}
                                forcedIcon={
                                    isLoadingIncidentTypes ? svgIcons.LoadingAnimation : null
                                }
                            />
                            {showIncidentTypeDropdown && <div
                                className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
                                onClick={() => showIncidentTypeDropdown && setIncidentTypeDropdown(false)}
                            />}
                            <div
                                className={`absolute mt-2 w-56 divide-y divide-gray-100 rounded-md border border-gray-100 bg-white shadow-lg z-modal top-0 transform -translate-y-[110%] right-0 md:translate-y-0 md:top-full ${showIncidentTypeDropdown ? "block" : "hidden"}`}
                                role="menu"
                            >

                                {
                                    incidentTypes?.selected?.length ?
                                        incidentTypes?.selected.map((type: IncidentItem) => (
                                            <div className="flex flex-col p-2 space-y-2" key={type.statuscode}>
                                                <CheckableListItem
                                                    id={type.statuscode.toString()}
                                                    title={type.description || type.statuscode.toString()}
                                                    checked={incidentQueryParams.statuscodes?.includes(type.statuscode.toString())}
                                                    handleChange={(e) => {
                                                        e.stopPropagation();
                                                        const statusCode = String(type.statuscode);
                                                        const prevStatusCodes = incidentQueryParams.statuscodes ?? [];

                                                        const updatedStatusCodes = prevStatusCodes.includes(statusCode)
                                                            ? prevStatusCodes.filter((code: any) => code !== statusCode)
                                                            : [...prevStatusCodes, statusCode];

                                                        dispatch(
                                                            setListingQueryParams({
                                                                ...listingQueryParams,
                                                                incidentsReview: {
                                                                    ...incidentQueryParams,
                                                                    statuscodes: updatedStatusCodes,
                                                                    page: 1
                                                                },
                                                            })
                                                        );

                                                    }}
                                                />
                                            </div>
                                        )) :
                                        <div className="flex flex-row p-2 my-2 justify-center text-sm">{t("no_incident_types")}</div>}
                            </div>
                        </div>
                        <div
                            className="relative"
                            onClick={() => setDispositionDropdown(!showDispostionDropdown)}
                        >
                            <AdminFormFieldDropdownButton
                                wrapperClass={`border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark hover:bg-accent-blue-dark hover:text-white${showDispostionDropdown ? " z-modal" : ""
                                    }`}
                                label="Disposition"
                                showDropdownIcon={showDispostionDropdown}
                                setShowDropdownIcon={setDispositionDropdown}
                            />
                            <SortingFilter
                                showSortingDropdown={showDispostionDropdown}
                                setShowSortingDropdown={setDispositionDropdown}
                                sortingOptions={[
                                    { value: "", label: "All Dispositions" },
                                    ...Object.entries(INCIDENT_DISPOSITIONS).map(([value, { label }]) => ({
                                        value,
                                        label,
                                    })),
                                ]}
                                selectedSorting={incidentQueryParams?.disposition ?? ""}
                                setSelectedSorting={(value) => {
                                    {
                                        dispatch(
                                            setListingQueryParams({
                                                ...listingQueryParams,
                                                incidentsReview: {
                                                    ...incidentQueryParams,
                                                    disposition: value
                                                },
                                            })
                                        );
                                    }
                                }}
                                hasCheckIcon={false}
                            />
                        </div>
                        <button
                            className="flex items-center bg-accent-blue-pale hover:bg-accent-blue-dark hover:text-white group py-2 px-3 rounded-lg gap-1 cursor-pointer"
                            type="button"
                            onClick={() => {
                                dispatch(
                                    setModalsData({
                                        ...modalsState,
                                        ...(accountHasGroupHierarchyEnabled === "1"
                                            ? { showHierarchyGroupSelector: true }
                                            : { showGroupSelector: true }),
                                    })
                                );
                            }}
                        >
                            <p className="font-medium text-sm leading-6">
                                {tAdmin("groups")}
                            </p>
                            <img
                                src={svgIcons.GroupFilterIcon}
                                className="w-4 h-4 group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                                alt="group-filter-icon"
                            />
                        </button>
                        <AppSearchBox
                            placeholder={t("search_placeholder")}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                debouncedSetSearchKeyword(e.target.value)
                            }}
                            onClear={
                                () => debouncedSetSearchKeyword("")
                            }
                            wrapperClassName="w-50"
                        />
                    </div>
                </div >
                <div className="pt-2">
                    {!!incidentQueryParams?.ordering && (
                        <SortingStatusWithReset
                            ordering={incidentQueryParams.ordering}
                            columnOrderingFields={columnOrderingFields}
                            onResetOrdering={() => handleOrderingChange(undefined)}
                            showOrder={true}
                        />
                    )}
                </div>

                <ModalEditListingColumns
                    columns={columns}
                    setColumns={setColumns}
                    lsKey={localStorageKeys.columns.IncidentReview}
                    modalType="showEditColumns"
                />
                <div className="flex-1 flex-grow flex flex-col py-4 relative">
                    <EditListingColumnsButton modalType="showEditColumns" />
                    <div className="flex-grow relative">
                        <AdminTable
                            columns={columns
                                .filter((item) => item?.show)
                                .map((item) => item.name)}
                            data={tableData}
                            isLoading={isLoadingTable}
                            colors={dispositionColors}
                            sortableColumns={columnOrderingFields}
                            onOrderingChange={handleOrderingChange}
                            ordering={incidentQueryParams.ordering}
                            draggableColumns={true}
                            onColumnOrderChange={(newOrder) => {
                                setColumns((prevColumns) => {
                                    const visibleColumns = prevColumns.filter((column) => column.show);

                                    // Reorder only the visible columns according to newOrder
                                    const reorderedVisibleColumns = newOrder.map(
                                        (name) => visibleColumns.find((column) => column.name === name)!
                                    );

                                    let visibleIndex = 0;

                                    // Put reordered visible columns back into their existing positions
                                    return prevColumns.map((column) => {
                                        if (!column.show) {
                                            return column;
                                        }
                                        return reorderedVisibleColumns[visibleIndex++];
                                    });
                                });
                                // Persist visible order
                                localStorage.setItem(localStorageKeys.columns.IncidentReview,JSON.stringify(newOrder))
                            }}
                        />
                    </div>
                </div>
                <Pagination
                    pageSize={incidentQueryParams.page_size}
                    handlePageSizeChange={(e: TSelectboxOption | null) => {
                        dispatch(
                            setListingQueryParams({
                                ...listingQueryParams,
                                incidentsReview: {
                                    ...incidentQueryParams,
                                    page: 1,
                                    page_size: parseInt(`${e?.value}`),
                                },
                            })
                        );
                    }}
                    totalPages={
                        tableResults?.results
                            ? Math.ceil(tableResults.count / (incidentQueryParams?.page_size))
                            : 1
                    }
                    forcePage={incidentQueryParams?.page -1}
                    handlePageClick={(data: TPaginationSelected) => {
                        dispatch(
                            setListingQueryParams({
                                ...listingQueryParams,
                                incidentsReview: {
                                    ...incidentQueryParams,
                                    page: data?.selected + 1,
                                },
                            })
                        );
                    }}
                    paginCurrentPage={incidentQueryParams.page}
                    paginItemsOnCurrentPage={tableResults?.results.length || 0}
                    paginTotalItemsCount={tableResults?.count}
                    wrapperClass={`flex mt-3 items-center`}
                    resultCountPrefixAtBottomLeft={
                        false
                    }
                />
            </div>
        </>
    );
}
export default ScreenIncidentListing;
